"use client";

import React, { useEffect, useState } from "react";
import Image from "next/image";
import dynamic from "next/dynamic";
import { SuccessMessageType } from "@/interface/types";

const EnqueryForm = dynamic(() => import("@/components/forms/EnqueryForm"), {
  ssr: false,
  loading: () => <FormPlaceholder />,
});

const FormPlaceholder = () => (
  <div className="flex flex-col gap-3 min-h-[300px] animate-pulse">
    <div className="h-11 w-full rounded-lg bg-[#d8e8f5]" />
    <div className="h-11 w-full rounded-lg bg-[#d8e8f5]" />
    <div className="h-11 w-full rounded-lg bg-[#d8e8f5]" />
    <div className="h-4 w-3/4 rounded bg-[#d8e8f5] mt-1" />
    <div className="h-11 w-full rounded-lg bg-[#b8d4f0] mt-2" />
  </div>
);

/* Mobile eyebrow: a notched ribbon that bleeds off the card's left edge. Two
   stacked copies of the same shape — the lighter one is 6px wider, so it shows
   as a sliver past the dark tip, which is how the asset is drawn. Inline SVG
   rather than an image keeps the point crisp at any density and costs no
   request. */
const CourseTag = ({ label }: { label: string }) => (
  <span className="md:hidden relative inline-flex items-center shrink-0 h-[30px] w-[143px] -ml-4 mb-2">
    <svg
      className="absolute left-0 top-0"
      width="143"
      height="30"
      viewBox="0 0 143 30"
      fill="none"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M0 0H130.536L143 15L130.536 30H0V0Z" fill="#336297" />
    </svg>
    <svg
      className="absolute left-0 top-0"
      width="137"
      height="30"
      viewBox="0 0 137 30"
      fill="none"
      aria-hidden="true"
      focusable="false"
    >
      <path
        d="M0 0H125.059L137 15L125.059 30H0V0Z"
        fill="url(#cma_course_tag)"
      />
      <defs>
        <linearGradient
          id="cma_course_tag"
          x1="0"
          y1="15"
          x2="132.592"
          y2="15"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#164B7F" />
          <stop offset="1" stopColor="#0B2A4D" />
        </linearGradient>
      </defs>
    </svg>
    <span className="relative z-10 pl-4 text-white text-[12px] font-bold uppercase tracking-[2px] leading-none">
      {label}
    </span>
  </span>
);

const CMACourseBanner = () => {
  const [mountForm, setMountForm] = useState(false);

  useEffect(() => {
    const win = window as Window & {
      requestIdleCallback?: (
        _cb: () => void,
        _opts?: { timeout: number },
      ) => number;
      cancelIdleCallback?: (_id: number) => void;
    };
    if (typeof win.requestIdleCallback === "function") {
      const id = win.requestIdleCallback(() => setMountForm(true), {
        timeout: 2000,
      });
      return () => win.cancelIdleCallback?.(id);
    }
    const id = window.setTimeout(() => setMountForm(true), 1200);
    return () => window.clearTimeout(id);
  }, []);

  return (
    <section className="w-full bg-white md:pb-6 md:pt-16 ">
      <div className=" md:max-w-[1340px] lg:px-7 mx-auto">
        {/* ── Gradient rounded card ── */}
        <div
          className="overflow-hidden md:rounded-[50px]"
          style={{
            background: "linear-gradient(270deg, #CEE6FF 0%, #F1F8FF 100%)",
          }}
        >
          <div
            className="md:grid flex flex-wrap md:items-stretch md:min-h-[380px]"
            style={{
              gridTemplateColumns:
                "minmax(0,1fr) minmax(300px,0.85fr) minmax(320px,0.9fr)",
            }}
          >
            {/* ── LEFT: text ── */}
            <div className="flex flex-col justify-center items-start text-left px-4 md:px-10 pt-7 pb-2 md:py-10 order-1 max-md:relative max-md:w-full max-md:pr-[36%]">
              <CourseTag label="CMA Course" />
              <p className="hidden md:block text-[14px] md:text-[16px] font-bold uppercase tracking-[2px]">
                <span className="text-[#39F]">CMA</span>{" "}
                <span className="text-[#333]">COURSE</span>
              </p>
              <h1 className="text-[#3399FF] text-[24px] md:text-[24px] font-bold leading-[25px] mb-2 md:mb-[14px]">
                Build your Career
                <br className="md:hidden block" /> with CMA
              </h1>
              {/* The photo occupies the right 52% of the card and its cutout
                  reaches furthest left (48vw) at the bottom, exactly where the
                  last lines of this paragraph sit. Capping the measure at
                  48vw minus the column's 16px padding keeps the text clear of
                  the arm on every phone width — a fixed px cap only works at
                  one viewport and overlaps on narrow devices. */}
              <p className="text-[#333] text-sm md:leading-normal leading-[1.3] max-w-[calc(48vw_-_16px)] md:max-w-[320px]">
                The Certified Management Accountant course is a globally
                recognized certification by IMA, USA. Explore the syllabus,
                eligibility, fees, duration, 2026 CBQ updates, and admission
                process. Learn with expert faculty, placement support, and CAIRA
                at Miles Education.
              </p>

              {/* ── MOBILE ONLY: photo resting on the form card ──
                  The source ships as a PNG behind a .webp name, so `f-webp`
                  is a real saving here: 77KB -> 20KB. 402px wide is ~2x the
                  rendered 52% column, so it stays sharp on retina without
                  paying for a 3x render. */}
              <div className="md:hidden absolute bottom-0 right-0 w-[52%]">
                <Image
                  src="https://ik.imagekit.io/mileseducation/miles_website/seo-pages/cma-course/mobile_version_banner.webp?tr=f-webp,q-80"
                  alt="AI-Ready CMA"
                  width={402}
                  height={504}
                  className="object-contain w-full h-auto"
                  priority
                />
              </div>
            </div>

            {/* ── CENTER: banner image ──
                `max-w-full` + `min-w-0` keep the photo inside its own grid
                column: at 380px wide it used to outgrow the column on 1024-1280
                screens and the overflow slid under the form card, which paints
                after it and clipped the right edge. `object-bottom` keeps the
                figure standing on the card floor when the column is narrow
                enough that contain has to scale it down. */}
            <div className="hidden md:flex items-end justify-center order-2 self-end min-w-0">
              <Image
                src="https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-course/cpa-course-banner.webp"
                alt="AI-Ready CPA"
                width={382}
                height={400}
                className="object-contain object-bottom w-auto max-w-full h-[370px] lg:h-[400px] block"
                priority
              />
            </div>

            {/* ── RIGHT: form card ── */}
            <div className="flex items-center justify-center order-3 px-4 md:px-6 py-4 md:py-8 max-md:w-full max-md:pt-0">
              <div className="bg-white rounded-2xl border border-[#DEE7F5] shadow-sm p-5 md:p-5 w-full max-w-[420px] md:max-w-none">
                {mountForm ? (
                  <EnqueryForm
                    pageName="CMA"
                    courseId={2}
                    darkBackground={false}
                    submitButtonName="Request a Callback"
                    formType="Banner Form"
                    isAStepperForm
                    successMessage={SuccessMessageType.ACCOUNTING}
                  />
                ) : (
                  <FormPlaceholder />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CMACourseBanner;
